import { useState, useEffect } from "react";
import { useLocation, useRoute } from "wouter";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { apiRequest } from '../lib/queryClient';
import { useAuth } from '../hooks/use-auth';
import { useToast } from '../hooks/use-toast';
import { useEstimates } from '../hooks/use-estimates';
import { format } from "date-fns";
import { 
  Ruler, 
  FileText, 
  Calculator, 
  Save, 
  ArrowLeft,
  Plus,
  Trash2,
  CheckCircle2,
  Clock,
  MapPin,
  User,
  Building2
} from "lucide-react";

// Simplified measurement tools - only practical ones
import FenceMeasurementTool from '../components/measurement/fence-measurement-tool';
import ProfessionalMeasurementTool from '../components/measurement/professional-measurement-tool';
import AdvancedMeasurement from '../components/measurement/advanced-measurement';

// UI Components
import Sidebar from '../components/layout/sidebar';
import MobileSidebar from '../components/layout/mobile-sidebar';
import TopNav from '../components/layout/top-nav';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Badge } from '../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';
import { Alert, AlertDescription } from '../components/ui/alert';

interface VisitNote {
  id: string;
  timestamp: Date;
  text: string;
  type: 'general' | 'measurement' | 'material' | 'issue' | 'client-request';
}

interface VisitMeasurement {
  id: string;
  serviceType: string;
  label: string;
  data: any;
  timestamp: Date;
}

interface SiteVisitData {
  eventId?: number;
  clientId: number;
  projectId?: number;
  notes: VisitNote[];
  measurements: VisitMeasurement[];
  visitDate: string;
  visitStatus: 'in-progress' | 'completed';
}

export default function SiteVisitPage() {
  const [location, setLocation] = useLocation();
  const { toast } = useToast();
  const { user } = useAuth();
  const queryClient = useQueryClient();
  const { createEstimateMutation } = useEstimates();

  // Get eventId and clientId from URL
  const urlParams = new URLSearchParams(window.location.search);
  const eventId = urlParams.get('eventId') ? parseInt(urlParams.get('eventId')!) : null;
  const clientIdFromUrl = urlParams.get('clientId') ? parseInt(urlParams.get('clientId')!) : null;

  // Visit data state
  const [visitData, setVisitData] = useState<SiteVisitData>({
    clientId: clientIdFromUrl || 0,
    notes: [],
    measurements: [],
    visitDate: new Date().toISOString().split('T')[0],
    visitStatus: 'in-progress'
  });

  const [activeTab, setActiveTab] = useState("notes");
  const [activeMeasurementTool, setActiveMeasurementTool] = useState<string | null>(null);
  const [currentMeasurements, setCurrentMeasurements] = useState<any[]>([]);
  const [showCreateEstimateDialog, setShowCreateEstimateDialog] = useState(false);
  const [estimateClientId, setEstimateClientId] = useState<number | null>(null);

  // Fetch event data if eventId provided
  const { data: eventData } = useQuery({
    queryKey: ['/api/protected/events', eventId],
    queryFn: async () => {
      if (!eventId) return null;
      const res = await apiRequest("GET", `/api/protected/events/${eventId}`);
      return await res.json();
    },
    enabled: !!eventId
  });

  // Fetch clients
  const { data: clients = [] } = useQuery({
    queryKey: ["/api/protected/clients"],
  });

  // Fetch projects
  const { data: projects = [] } = useQuery({
    queryKey: ["/api/protected/projects"],
    enabled: !!visitData.clientId
  });

  // Update visit data when event loads
  useEffect(() => {
    if (eventData) {
      setVisitData(prev => ({
        ...prev,
        eventId: eventData.id,
        clientId: eventData.clientId || prev.clientId,
        projectId: eventData.projectId || prev.projectId,
        visitDate: eventData.startTime ? new Date(eventData.startTime).toISOString().split('T')[0] : prev.visitDate
      }));
    }
  }, [eventData]);

  // Save visit data (notes + measurements)
  const saveVisitMutation = useMutation({
    mutationFn: async (data: SiteVisitData) => {
      // Save notes to event
      if (data.eventId) {
        await apiRequest("PATCH", `/api/protected/events/${data.eventId}`, {
          notes: data.notes.map(n => `[${format(n.timestamp, 'PPp')}] ${n.type.toUpperCase()}: ${n.text}`).join('\n\n')
        });
      }

      // Save measurements to property_measurements table
      for (const measurement of data.measurements) {
        await apiRequest("POST", "/api/protected/property-measurements", {
          clientId: data.clientId,
          projectId: data.projectId || null,
          serviceType: measurement.serviceType,
          measurementData: measurement.data,
          notes: `Visit measurement: ${measurement.label}`,
          measuredAt: measurement.timestamp.toISOString()
        });
      }

      return { success: true };
    },
    onSuccess: () => {
      toast({
        title: "Visit data saved",
        description: "Notes and measurements have been saved successfully."
      });
      queryClient.invalidateQueries({ queryKey: ["/api/protected/events"] });
      queryClient.invalidateQueries({ queryKey: ["/api/protected/property-measurements"] });
    },
    onError: (error: any) => {
      toast({
        title: "Failed to save",
        description: error.message || "Please try again",
        variant: "destructive"
      });
    }
  });

  // Add note
  const addNote = (text: string, type: VisitNote['type'] = 'general') => {
    if (!text.trim()) return;

    const newNote: VisitNote = {
      id: `note-${Date.now()}`,
      timestamp: new Date(),
      text: text.trim(),
      type
    };

    setVisitData(prev => ({
      ...prev,
      notes: [...prev.notes, newNote]
    }));
  };

  // Add measurement
  const addMeasurement = (serviceType: string, label: string, data: any) => {
    const newMeasurement: VisitMeasurement = {
      id: `measurement-${Date.now()}`,
      serviceType,
      label,
      data,
      timestamp: new Date()
    };

    setVisitData(prev => ({
      ...prev,
      measurements: [...prev.measurements, newMeasurement]
    }));

    setCurrentMeasurements([]);
    setActiveMeasurementTool(null);
  };

  // Handle measurement tool results
  const handleMeasurementsChange = (measurements: any[]) => {
    setCurrentMeasurements(measurements);
  };

  // Save current measurements
  const saveCurrentMeasurements = () => {
    if (currentMeasurements.length === 0) {
      toast({
        title: "No measurements",
        description: "Please take some measurements first.",
        variant: "destructive"
      });
      return;
    }

    // Determine service type from measurements or let user select
    const serviceType = currentMeasurements[0]?.type || 'other';
    const label = `Measurement ${visitData.measurements.length + 1}`;

    addMeasurement(serviceType, label, currentMeasurements);
    
    toast({
      title: "Measurements saved",
      description: `${currentMeasurements.length} measurement(s) added to visit.`
    });
  };

  // Create estimate from visit data
  const createEstimateFromVisit = async () => {
    if (!estimateClientId) {
      toast({
        title: "Client required",
        description: "Please select a client for the estimate.",
        variant: "destructive"
      });
      return;
    }

    if (visitData.measurements.length === 0 && visitData.notes.length === 0) {
      toast({
        title: "No data",
        description: "Please add notes or measurements before creating an estimate.",
        variant: "destructive"
      });
      return;
    }

    // Generate estimate description from notes and measurements
    const notesText = visitData.notes.map(n => 
      `[${format(n.timestamp, 'PPp')}] ${n.type.toUpperCase()}: ${n.text}`
    ).join('\n\n');

    const measurementsText = visitData.measurements.map(m => 
      `${m.label}: ${JSON.stringify(m.data)}`
    ).join('\n');

    const description = `Site Visit Estimate\n\nVisit Date: ${format(new Date(visitData.visitDate), 'PP')}\n\nNotes:\n${notesText}\n\nMeasurements:\n${measurementsText}`;

    // Generate estimate number
    const today = new Date();
    const year = today.getFullYear();
    const month = (today.getMonth() + 1).toString().padStart(2, '0');
    const random = Math.floor(Math.random() * 900) + 100;
    const estimateNumber = `EST-${year}${month}-${random}`;

    // Create estimate items from measurements
    const items = visitData.measurements.map((m, idx) => ({
      serviceType: m.serviceType,
      description: m.label,
      quantity: '1',
      unit: 'each',
      unitPrice: '0.00',
      amount: '0.00',
      notes: JSON.stringify(m.data)
    }));

    // If no items, add a placeholder
    if (items.length === 0) {
      items.push({
        serviceType: 'other',
        description: 'Site visit - estimate to be determined',
        quantity: '1',
        unit: 'each',
        unitPrice: '0.00',
        amount: '0.00',
        notes: notesText
      });
    }

    const estimateData = {
      clientId: estimateClientId,
      estimateNumber,
      issueDate: today.toISOString(),
      expiryDate: new Date(today.getTime() + 30 * 24 * 60 * 60 * 1000).toISOString(),
      status: 'draft',
      description,
      notes: `Created from site visit on ${format(new Date(visitData.visitDate), 'PP')}`,
      subtotal: '0.00',
      tax: '0.00',
      discount: '0.00',
      total: '0.00',
      items
    };

    try {
      await createEstimateMutation.mutateAsync(estimateData);
      toast({
        title: "Estimate created",
        description: "Estimate has been created from visit data."
      });
      setShowCreateEstimateDialog(false);
      setLocation(`/estimates`);
    } catch (error: any) {
      toast({
        title: "Failed to create estimate",
        description: error.message || "Please try again",
        variant: "destructive"
      });
    }
  };

  const selectedClient = clients.find((c: any) => c.id === visitData.clientId);
  const selectedProject = projects.find((p: any) => p.id === visitData.projectId);

  return (
    <div className="remodra-layout">
      <div className="hidden md:block">
        <Sidebar />
      </div>
      <MobileSidebar />
      <div className="remodra-main-mobile md:ml-64">
        <TopNav />
        <div className="remodra-content">
          <div className="w-full px-4 sm:px-6 lg:px-8 pt-4 pb-6">
            {/* Header */}
            <div className="mb-6">
              <Button variant="ghost" size="sm" className="mb-4" onClick={() => setLocation('/calendar')}>
                <ArrowLeft className="h-4 w-4 mr-2" />
                Back to Calendar
              </Button>
              <div className="flex items-center justify-between">
                <div>
                  <h1 className="text-3xl font-bold text-foreground">Site Visit</h1>
                  <p className="text-muted-foreground">
                    Take notes and measurements, then create an estimate
                  </p>
                </div>
                <div className="flex gap-2">
                  <Button
                    variant="outline"
                    onClick={() => saveVisitMutation.mutate(visitData)}
                    disabled={saveVisitMutation.isPending}
                  >
                    <Save className="h-4 w-4 mr-2" />
                    Save Visit
                  </Button>
                  <Button
                    onClick={() => setShowCreateEstimateDialog(true)}
                    disabled={visitData.measurements.length === 0 && visitData.notes.length === 0}
                  >
                    <Calculator className="h-4 w-4 mr-2" />
                    Create Estimate
                  </Button>
                </div>
              </div>
            </div>

            {/* Visit Info Card */}
            <Card className="mb-6">
              <CardHeader>
                <CardTitle>Visit Information</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div className="flex items-center gap-2">
                    <User className="h-4 w-4 text-muted-foreground" />
                    <div>
                      <Label className="text-xs text-muted-foreground">Client</Label>
                      <p className="font-medium">
                        {selectedClient ? `${selectedClient.firstName} ${selectedClient.lastName}` : 'Not selected'}
                      </p>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <Building2 className="h-4 w-4 text-muted-foreground" />
                    <div>
                      <Label className="text-xs text-muted-foreground">Project</Label>
                      <p className="font-medium">
                        {selectedProject ? selectedProject.name : 'None'}
                      </p>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <Clock className="h-4 w-4 text-muted-foreground" />
                    <div>
                      <Label className="text-xs text-muted-foreground">Visit Date</Label>
                      <p className="font-medium">{format(new Date(visitData.visitDate), 'PP')}</p>
                    </div>
                  </div>
                </div>
                {eventData && (
                  <div className="mt-4 flex items-center gap-2">
                    <MapPin className="h-4 w-4 text-muted-foreground" />
                    <div>
                      <Label className="text-xs text-muted-foreground">Location</Label>
                      <p className="font-medium">
                        {eventData.address ? `${eventData.address}, ${eventData.city || ''} ${eventData.state || ''}` : 'No address'}
                      </p>
                    </div>
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Main Tabs */}
            <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
              <TabsList className="grid w-full grid-cols-2 mb-6">
                <TabsTrigger value="notes" className="flex items-center gap-2">
                  <FileText className="h-4 w-4" />
                  Notes ({visitData.notes.length})
                </TabsTrigger>
                <TabsTrigger value="measurements" className="flex items-center gap-2">
                  <Ruler className="h-4 w-4" />
                  Measurements ({visitData.measurements.length})
                </TabsTrigger>
              </TabsList>

              {/* Notes Tab */}
              <TabsContent value="notes" className="space-y-4">
                <Card>
                  <CardHeader>
                    <CardTitle>Visit Notes</CardTitle>
                    <CardDescription>
                      Document observations, discussions, materials discussed, and any issues
                    </CardDescription>
                  </CardHeader>
                  <CardContent className="space-y-4">
                    {/* Quick Note Input */}
                    <div className="space-y-2">
                      <Label>Add Note</Label>
                      <div className="flex gap-2">
                        <Textarea
                          placeholder="Enter your note here..."
                          className="flex-1"
                          onKeyDown={(e) => {
                            if (e.key === 'Enter' && e.ctrlKey) {
                              const text = (e.target as HTMLTextAreaElement).value;
                              addNote(text);
                              (e.target as HTMLTextAreaElement).value = '';
                            }
                          }}
                        />
                        <div className="flex flex-col gap-2">
                          <Button
                            size="sm"
                            onClick={(e) => {
                              const textarea = e.currentTarget.parentElement?.previousElementSibling as HTMLTextAreaElement;
                              if (textarea) {
                                addNote(textarea.value);
                                textarea.value = '';
                              }
                            }}
                          >
                            <Plus className="h-4 w-4" />
                          </Button>
                        </div>
                      </div>
                      <p className="text-xs text-muted-foreground">
                        Press Ctrl+Enter to add note quickly
                      </p>
                    </div>

                    {/* Notes List */}
                    <div className="space-y-2">
                      {visitData.notes.length === 0 ? (
                        <Alert>
                          <AlertDescription>
                            No notes yet. Add notes about the visit, materials discussed, client requests, etc.
                          </AlertDescription>
                        </Alert>
                      ) : (
                        visitData.notes.map((note) => (
                          <Card key={note.id} className="p-3">
                            <div className="flex items-start justify-between">
                              <div className="flex-1">
                                <div className="flex items-center gap-2 mb-1">
                                  <Badge variant="outline" className="text-xs">
                                    {note.type}
                                  </Badge>
                                  <span className="text-xs text-muted-foreground">
                                    {format(note.timestamp, 'PPp')}
                                  </span>
                                </div>
                                <p className="text-sm">{note.text}</p>
                              </div>
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => {
                                  setVisitData(prev => ({
                                    ...prev,
                                    notes: prev.notes.filter(n => n.id !== note.id)
                                  }));
                                }}
                              >
                                <Trash2 className="h-4 w-4" />
                              </Button>
                            </div>
                          </Card>
                        ))
                      )}
                    </div>
                  </CardContent>
                </Card>
              </TabsContent>

              {/* Measurements Tab */}
              <TabsContent value="measurements" className="space-y-4">
                <Card>
                  <CardHeader>
                    <CardTitle>Take Measurements</CardTitle>
                    <CardDescription>
                      Use measurement tools to capture dimensions for your estimate
                    </CardDescription>
                  </CardHeader>
                  <CardContent className="space-y-4">
                    {/* Tool Selection */}
                    {!activeMeasurementTool && (
                      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                        <Card
                          className="cursor-pointer hover:shadow-md transition-shadow"
                          onClick={() => setActiveMeasurementTool('fence')}
                        >
                          <CardHeader>
                            <CardTitle className="text-sm">Fence Measurement</CardTitle>
                            <CardDescription className="text-xs">
                              For fence projects with gates and posts
                            </CardDescription>
                          </CardHeader>
                        </Card>
                        <Card
                          className="cursor-pointer hover:shadow-md transition-shadow"
                          onClick={() => setActiveMeasurementTool('professional')}
                        >
                          <CardHeader>
                            <CardTitle className="text-sm">Quick Input</CardTitle>
                            <CardDescription className="text-xs">
                              Fast dimension entry (rectangles, L-shapes, linear)
                            </CardDescription>
                          </CardHeader>
                        </Card>
                        <Card
                          className="cursor-pointer hover:shadow-md transition-shadow"
                          onClick={() => setActiveMeasurementTool('advanced')}
                        >
                          <CardHeader>
                            <CardTitle className="text-sm">Image Measurement</CardTitle>
                            <CardDescription className="text-xs">
                              Upload image and measure from photo
                            </CardDescription>
                          </CardHeader>
                        </Card>
                      </div>
                    )}

                    {/* Active Tool */}
                    {activeMeasurementTool && (
                      <div className="space-y-4">
                        <div className="flex items-center justify-between">
                          <h3 className="font-medium">
                            {activeMeasurementTool === 'fence' && 'Fence Measurement Tool'}
                            {activeMeasurementTool === 'professional' && 'Professional Measurement Tool'}
                            {activeMeasurementTool === 'advanced' && 'Advanced Measurement Tool'}
                          </h3>
                          <div className="flex gap-2">
                            {currentMeasurements.length > 0 && (
                              <Button
                                variant="outline"
                                size="sm"
                                onClick={saveCurrentMeasurements}
                              >
                                <Save className="h-4 w-4 mr-2" />
                                Save Measurements ({currentMeasurements.length})
                              </Button>
                            )}
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => {
                                setActiveMeasurementTool(null);
                                setCurrentMeasurements([]);
                              }}
                            >
                              Close Tool
                            </Button>
                          </div>
                        </div>

                        {activeMeasurementTool === 'fence' && (
                          <FenceMeasurementTool
                            onMeasurementsChange={handleMeasurementsChange}
                            serviceUnit="ft"
                          />
                        )}
                        {activeMeasurementTool === 'professional' && (
                          <ProfessionalMeasurementTool
                            onMeasurementsChange={handleMeasurementsChange}
                            serviceUnit="sqft"
                          />
                        )}
                        {activeMeasurementTool === 'advanced' && (
                          <AdvancedMeasurement
                            onMeasurementsChange={handleMeasurementsChange}
                            initialScale={1}
                            unit="ft"
                            showCostEstimates={false}
                            defaultServiceType="roofing"
                          />
                        )}
                      </div>
                    )}

                    {/* Saved Measurements List */}
                    {visitData.measurements.length > 0 && (
                      <div className="space-y-2 mt-6">
                        <h3 className="font-medium">Saved Measurements</h3>
                        {visitData.measurements.map((measurement) => (
                          <Card key={measurement.id} className="p-3">
                            <div className="flex items-start justify-between">
                              <div className="flex-1">
                                <div className="flex items-center gap-2 mb-1">
                                  <Badge variant="outline" className="text-xs">
                                    {measurement.serviceType}
                                  </Badge>
                                  <span className="font-medium text-sm">{measurement.label}</span>
                                  <span className="text-xs text-muted-foreground">
                                    {format(measurement.timestamp, 'PPp')}
                                  </span>
                                </div>
                                <p className="text-xs text-muted-foreground">
                                  {JSON.stringify(measurement.data).substring(0, 100)}...
                                </p>
                              </div>
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => {
                                  setVisitData(prev => ({
                                    ...prev,
                                    measurements: prev.measurements.filter(m => m.id !== measurement.id)
                                  }));
                                }}
                              >
                                <Trash2 className="h-4 w-4" />
                              </Button>
                            </div>
                          </Card>
                        ))}
                      </div>
                    )}
                  </CardContent>
                </Card>
              </TabsContent>
            </Tabs>
          </div>
        </div>
      </div>

      {/* Create Estimate Dialog */}
      <Dialog open={showCreateEstimateDialog} onOpenChange={setShowCreateEstimateDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Create Estimate from Visit</DialogTitle>
            <DialogDescription>
              Create an estimate using the notes and measurements from this visit.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label>Client</Label>
              <Select
                value={estimateClientId?.toString() || ''}
                onValueChange={(value) => setEstimateClientId(parseInt(value))}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Select client" />
                </SelectTrigger>
                <SelectContent>
                  {clients.map((client: any) => (
                    <SelectItem key={client.id} value={client.id.toString()}>
                      {client.firstName} {client.lastName}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <Alert>
              <AlertDescription>
                <strong>Summary:</strong><br />
                Notes: {visitData.notes.length}<br />
                Measurements: {visitData.measurements.length}
              </AlertDescription>
            </Alert>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowCreateEstimateDialog(false)}>
              Cancel
            </Button>
            <Button onClick={createEstimateFromVisit} disabled={!estimateClientId}>
              Create Estimate
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

